/**
 * Converts supported public video URLs to canonical HTTPS embed URLs.
 * Hostnames and paths are validated separately so lookalike domains cannot
 * pass by containing a provider name in an unrelated URL.
 */

export type VideoProviderT =
  "youtube" | "vimeo" | "dailymotion" | "instagram" | "unknown";

const YOUTUBE_HOSTS = new Set([
  "youtube.com",
  "www.youtube.com",
  "m.youtube.com",
  "youtube-nocookie.com",
  "www.youtube-nocookie.com",
  "youtu.be",
]);

const VIMEO_HOSTS = new Set(["vimeo.com", "www.vimeo.com", "player.vimeo.com"]);

const DAILYMOTION_HOSTS = new Set([
  "dailymotion.com",
  "www.dailymotion.com",
  "dai.ly",
]);

const INSTAGRAM_HOSTS = new Set(["instagram.com", "www.instagram.com"]);

const YOUTUBE_ID_PATTERN = /^[a-zA-Z0-9_-]{11}$/;
const VIMEO_ID_PATTERN = /^\d+$/;
const DAILYMOTION_ID_PATTERN = /^[a-zA-Z0-9]+$/;
const INSTAGRAM_ID_PATTERN = /^[a-zA-Z0-9_-]+$/;

function parseHttpsUrl(input: unknown): URL | null {
  if (typeof input !== "string" || input.trim().length === 0) {
    return null;
  }

  try {
    const parsedUrl = new URL(input.trim());
    return parsedUrl.protocol === "https:" ? parsedUrl : null;
  } catch {
    return null;
  }
}

function getPathSegments(url: URL): string[] {
  return url.pathname.split("/").filter(Boolean);
}

function getYouTubeVideoId(url: URL): string | null {
  const segments = getPathSegments(url);
  let videoId: string | null = null;

  if (url.hostname === "youtu.be") {
    videoId = segments[0] ?? null;
  } else if (segments[0] === "watch") {
    videoId = url.searchParams.get("v");
  } else if (segments[0] === "embed" || segments[0] === "shorts") {
    videoId = segments[1] ?? null;
  }

  return videoId && YOUTUBE_ID_PATTERN.test(videoId) ? videoId : null;
}

function getVimeoVideoId(url: URL): string | null {
  const segments = getPathSegments(url);
  const videoId =
    url.hostname === "player.vimeo.com" && segments[0] === "video"
      ? segments[1]
      : segments.length === 1
        ? segments[0]
        : null;

  return videoId && VIMEO_ID_PATTERN.test(videoId) ? videoId : null;
}

function getDailymotionVideoId(url: URL): string | null {
  const segments = getPathSegments(url);
  let videoId: string | null = null;

  if (url.hostname === "dai.ly") {
    videoId = segments[0] ?? null;
  } else if (segments[0] === "video") {
    videoId = segments[1] ?? null;
  } else if (segments[0] === "embed" && segments[1] === "video") {
    videoId = segments[2] ?? null;
  }

  return videoId && DAILYMOTION_ID_PATTERN.test(videoId) ? videoId : null;
}

function getInstagramReelId(url: URL): string | null {
  const segments = getPathSegments(url);
  const reelId = segments[0] === "reel" ? segments[1] : null;

  return reelId && INSTAGRAM_ID_PATTERN.test(reelId) ? reelId : null;
}

export function detectProvider(url: string): VideoProviderT {
  const parsedUrl = parseHttpsUrl(url);
  if (!parsedUrl) return "unknown";

  if (YOUTUBE_HOSTS.has(parsedUrl.hostname)) return "youtube";
  if (VIMEO_HOSTS.has(parsedUrl.hostname)) return "vimeo";
  if (DAILYMOTION_HOSTS.has(parsedUrl.hostname)) return "dailymotion";
  if (INSTAGRAM_HOSTS.has(parsedUrl.hostname)) return "instagram";

  return "unknown";
}

/**
 * Returns a canonical embed URL or null when the URL is not an exact,
 * supported provider URL.
 */
export function convertToEmbedUrl(url: string): string | null {
  const parsedUrl = parseHttpsUrl(url);
  if (!parsedUrl) return null;

  const provider = detectProvider(parsedUrl.href);

  if (provider === "youtube") {
    const videoId = getYouTubeVideoId(parsedUrl);
    return videoId ? `https://www.youtube.com/embed/${videoId}` : null;
  }

  if (provider === "vimeo") {
    const videoId = getVimeoVideoId(parsedUrl);
    return videoId ? `https://player.vimeo.com/video/${videoId}` : null;
  }

  if (provider === "dailymotion") {
    const videoId = getDailymotionVideoId(parsedUrl);
    return videoId
      ? `https://www.dailymotion.com/embed/video/${videoId}`
      : null;
  }

  if (provider === "instagram") {
    const reelId = getInstagramReelId(parsedUrl);
    return reelId
      ? `https://www.instagram.com/reel/${reelId}/embed/captioned/`
      : null;
  }

  return null;
}

export function wasUrlConverted(
  originalUrl: string,
  convertedUrl: string | null
): boolean {
  return convertedUrl !== null && originalUrl.trim() !== convertedUrl.trim();
}
